import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRootDispatch, useRootSelector } from '../../../../store/hooks';
import { selectSiteContentEntriesData } from '../../../../store/features/siteContent';
import SiteLayout from '../../../layouts/SiteLayout/SiteLayout';
import {
  IGFEntryProfileProps,
  fetchIGFEntryByYear,
  selectEntryDetails,
  selectIGFEntriesStatus,
} from '../../../../store/features/igfEntries';
import Spinner from '../IGFEntryView/Spinner';
import { Anchor } from '../../../shared/Anchor';
import { HTMLtoJSX } from '../../../../helpers';
import { ImageTag } from '../../../shared/ImageTag/ImageTag';
import ExternalError from '../IGFEntryView/ExternalError';
import VideoPlayer from '../../../shared/VideoPlayer';

import styles from '../IGFEntryProfileView/EntryProfileView.module.scss';

interface ProfileImageBlockProps {
  caption?: string;
  imageSrc: string;
}

export const VideoSources = {
  YouTube: 'YOUTUBE',
  Vimeo: 'VIMEO',
};

type VideoSourceType = keyof typeof VideoSources;

const ProfileImageBlock = (props: ProfileImageBlockProps) => {
  const { caption, imageSrc } = props;
  return (
    <>
      <ImageTag
        className={styles.profileImageDesktop}
        alt={caption}
        src={imageSrc}
        imgix={false}
        lazy={false}
      />
      <div className={styles.imageCaption}>{caption}</div>
    </>
  );
};

export default function IGFEntryProfileView() {
  const dispatch = useRootDispatch();
  const { t } = useTranslation();
  const { entryName }: { entryName: string } = useParams();
  const { url, year } = useRootSelector(selectSiteContentEntriesData);
  const entriesStatus = useRootSelector(selectIGFEntriesStatus);
  const profileData = useRootSelector((state) =>
    selectEntryDetails(state, entryName),
  );
  const {
    name,
    creator,
    school,
    description,
    isStudent,
    image: { caption, thumb_1000: imageSrc } = {},
    video: { videoid: videoId, caption: videoCaption, type } = {},
    url: profileUrl,
  } = profileData as IGFEntryProfileProps;

  const studentLabel =
    isStudent === '1'
      ? t('site.year.profile.page.student.entrant', {
          0: year,
        })
      : t('site.year.profile.page.entrant.year', {
          0: year,
        });

  useEffect(() => {
    dispatch(fetchIGFEntryByYear({ dataUrl: url }));
  }, [dispatch, url]);

  const loading = entriesStatus === 'pending';
  const showError = entriesStatus === 'rejected';
  const showContent = entriesStatus === 'fulfilled';

  return (
    // @ts-ignore
    <SiteLayout
      helmet={{
        title: name,
      }}
    >
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            {loading && (
              <div className="text-center">
                <Spinner />
              </div>
            )}
            {showError && <ExternalError />}
            {showContent && (
              <div className={styles.wrapper}>
                <div className="row">
                  <div className="col-xs-12 col-md-6">
                    <div className={styles.mainInfoWrapper}>
                      <div className={styles.profileTitle}>
                        {HTMLtoJSX(name)}
                      </div>
                      <div className={styles.profileCreator}>
                        {HTMLtoJSX(creator)}
                      </div>
                      <div className={styles.profileSchool}>
                        {HTMLtoJSX(school)}
                      </div>
                      <div className={styles.studentLabel}>{studentLabel}</div>
                      {imageSrc && (
                        <div className="hidden-md hidden-lg">
                          <ProfileImageBlock
                            caption={caption}
                            imageSrc={imageSrc}
                          />
                        </div>
                      )}
                      <div className={styles.profileDescription}>
                        {HTMLtoJSX(description)}
                      </div>
                      <div className={styles.linksWrapper}>
                        {url && (
                          <Anchor
                            to={profileUrl}
                            className={styles.webLink}
                            data-testid="profile-link"
                          >
                            <span className={styles.webLinkText}>
                              {t('site.year.profile.page.visit-website.link')}
                            </span>
                            <span className="fa-solid fa-arrow-right" />
                          </Anchor>
                        )}
                      </div>
                    </div>
                  </div>
                  {imageSrc && (
                    <div className="hidden-xs hidden-sm col-md-6">
                      <ProfileImageBlock
                        caption={caption}
                        imageSrc={imageSrc}
                      />
                    </div>
                  )}
                </div>
                {videoId && VideoSources[type as VideoSourceType] && (
                  <div className="row">
                    <div className="col-xs-12">
                      <div className={styles.videoWrapper}>
                        <VideoPlayer
                          videoSource={VideoSources[type as VideoSourceType]}
                          videoId={videoId}
                          title={videoCaption}
                          autoplay={false}
                        />
                      </div>
                      {videoCaption && <div>{HTMLtoJSX(videoCaption)}</div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
